import { useState } from 'react'
import { Ban, Play, Plus, Trash2 } from 'lucide-react'
import {
  useActivateAgency,
  useAgencies,
  useAssignPlan,
  useCreateAgency,
  useDeleteAgency,
  usePlans,
  useSuspendAgency,
} from '@/features/platform/usePlatform'
import { apiErrorMessage } from '@/features/settings/apiErrorMessage'
import ConfirmButton from '@/components/ui/ConfirmButton'
import IndexToolbar from '@/components/IndexToolbar'
import { matchesQuery } from '@/lib/matchesQuery'
import PageContainer from '@/components/layout/PageContainer'

const inputCls = 'px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40'

const STATUS_STYLE: Record<string, string> = {
  active: 'bg-success/10 text-success',
  trial: 'bg-brand-primary/10 text-brand-primary',
  suspended: 'bg-danger/10 text-danger',
}

export default function Platform() {
  const { data: agencies } = useAgencies()
  const [agencyQuery, setAgencyQuery] = useState('')
  const shownAgencies = (agencies ?? []).filter((a) => matchesQuery(agencyQuery, a.name, a.slug, a.plan))
  const { data: plans } = usePlans()
  const create = useCreateAgency()
  const suspend = useSuspendAgency()
  const activate = useActivateAgency()
  const del = useDeleteAgency()
  const assignPlan = useAssignPlan()

  const [form, setForm] = useState({ agencyName: '', slug: '', adminFullName: '', adminEmail: '', adminPassword: '' })
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }))

  const canCreate =
    form.agencyName.trim() && /^[A-Za-z0-9-]{2,40}$/.test(form.slug) &&
    form.adminFullName.trim() && form.adminEmail.trim() && form.adminPassword.length >= 10

  function submit() {
    if (!canCreate) return
    create.mutate(form, { onSuccess: () => setForm({ agencyName: '', slug: '', adminFullName: '', adminEmail: '', adminPassword: '' }) })
  }

  return (
    <PageContainer width="full">
      <div className="mb-4">
        <h1 className="text-xl font-semibold text-brand-darkest">Platform console</h1>
        <p className="text-sm text-gray-500">Manage agencies (workspaces). Metadata only — no access to a tenant&apos;s data.</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
        {/* agency list */}
        <div>
        <IndexToolbar query={agencyQuery} onQueryChange={setAgencyQuery} placeholder="Search agencies by name or address…" shown={shownAgencies.length} total={(agencies ?? []).length} />
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                <th className="px-4 py-2.5 font-medium">Agency</th>
                <th className="px-3 py-2.5 font-medium">Status</th>
                <th className="px-3 py-2.5 font-medium">Plan</th>
                <th className="px-3 py-2.5 font-medium tabular-nums">Users</th>
                <th className="px-3 py-2.5 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {shownAgencies.map((a) => (
                <tr key={a.id} className="border-b border-gray-50 last:border-0">
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-brand-darkest">{a.name}</div>
                    <div className="text-xs text-gray-400">{a.slug} · {a.plan}</div>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_STYLE[a.status] ?? 'bg-gray-100 text-gray-500'}`}>
                      {a.status}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <select
                      className={inputCls}
                      value={a.planKey ?? ''}
                      onChange={(e) => assignPlan.mutate({ id: a.id, planKey: e.target.value || null })}
                    >
                      <option value="">No plan</option>
                      {(plans ?? []).filter((p) => p.active || p.key === a.planKey).map((p) => (
                        <option key={p.key} value={p.key}>{p.name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2.5 tabular-nums text-gray-600">{a.userCount}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center justify-end gap-1">
                      {a.status === 'suspended' ? (
                        <button className="p-1.5 text-gray-400 hover:text-success" title="Reactivate"
                          onClick={() => activate.mutate(a.id)}>
                          <Play size={15} />
                        </button>
                      ) : (
                        <ConfirmButton className="p-1.5 text-gray-400 hover:text-danger" title="Suspend"
                          onConfirm={() => suspend.mutate(a.id)}>
                          <Ban size={15} />
                        </ConfirmButton>
                      )}
                      {a.status === 'suspended' && (
                        <ConfirmButton className="p-1.5 text-gray-400 hover:text-danger" title="Delete permanently"
                          onConfirm={() => del.mutate(a.id)}>
                          <Trash2 size={15} />
                        </ConfirmButton>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {(agencies ?? []).length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">No agencies yet.</td></tr>
              )}
              {(agencies ?? []).length > 0 && shownAgencies.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">No agencies match “{agencyQuery}”.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        </div>

        {/* create agency */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-2.5 self-start">
          <div className="text-sm font-semibold text-brand-darkest">New agency</div>
          <input className={`${inputCls} w-full`} placeholder="Agency name" value={form.agencyName} onChange={set('agencyName')} />
          <input className={`${inputCls} w-full`} placeholder="Slug (letters, digits, hyphen)" value={form.slug} onChange={set('slug')} />
          <div className="pt-1 text-xs text-gray-400">First admin</div>
          <input className={`${inputCls} w-full`} placeholder="Admin full name" value={form.adminFullName} onChange={set('adminFullName')} />
          <input className={`${inputCls} w-full`} type="email" placeholder="Admin email" value={form.adminEmail} onChange={set('adminEmail')} />
          <input className={`${inputCls} w-full`} type="password" placeholder="Temp password (min 10 chars)" value={form.adminPassword} onChange={set('adminPassword')} />
          {create.error && <p className="text-danger text-xs">{apiErrorMessage(create.error)}</p>}
          <button
            className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg bg-brand-primary text-white disabled:opacity-50"
            disabled={!canCreate || create.isPending}
            onClick={submit}
          >
            <Plus size={14} /> Create agency
          </button>
        </div>
      </div>
    </PageContainer>
  )
}
